import { useEffect, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { APP_NAME, ENGINE_LABELS } from "@/config";
import { Page, PageHeader, Card, Segmented, Label11, Loading, usd } from "@/components/common";
import { EngineMark } from "@/components/ProviderLogo";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { KIND_LABELS, kindOf, isDocuments } from "@/lib/workKinds";

// design_handoff_crewbench/screens/New Session.dc.html: project, objective, engine and cap, with what the session starts
// with beside them. POST /sessions makes it (locked); a cap other than the backend's default is then set with
// PUT /sessions/{id}/cap.
const DEFAULT_CAP = 5;  // session_routes.create_session's budget_cap_usd
const STATUS = { todo: "To do", "in progress": "In progress", review: "In review" };
const PROVIDER = { claude: "Anthropic", codex: "OpenAI", muse: "Meta" };

// an option card: a hairline box; the chosen one gets a 2px edge laid over it
function Option({ on, disabled, onPick, children, testid }) {
  return (
    <button type="button" role="radio" aria-checked={on} disabled={disabled} onClick={onPick} data-testid={testid}
      className={cn("relative w-full text-left flex items-start gap-2.5 px-3.5 py-3 rounded-lg border transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
        disabled ? "opacity-40 cursor-not-allowed" : "hover:bg-gr-hover")}>
      {on && <span className="absolute -inset-px border-2 border-foreground rounded-lg pointer-events-none" aria-hidden="true" />}
      {children}
    </button>
  );
}
const Radio = ({ on }) => <span aria-hidden="true" className={cn("w-4 h-4 mt-0.5 rounded-full shrink-0 box-border", on ? "border-[5px] border-foreground" : "border border-gr-control-border")} />;

function ObjectiveOption({ o, on, onPick }) {
  const { data: r } = useQuery({ queryKey: ["rollup", o.id], enabled: o.budget_usd > 0,
                                 queryFn: async () => (await api.get(`/objectives/${o.id}/rollup`)).data });
  const left = o.budget_usd > 0 && r ? `${usd(Math.max(0, o.budget_usd - r.spent_usd))} of ${usd(o.budget_usd)} left` : null;
  return (
    <Option on={on} onPick={onPick} testid={`new-session-objective-${o.id}`}>
      <Radio on={on} />
      <span className="flex flex-col min-w-0"><span className="font-semibold">{o.title}</span>
        <span className="text-sm text-muted-foreground tabular-nums">{[STATUS[o.status] || o.status, left].filter(Boolean).join(" · ")}</span></span>
    </Option>
  );
}

export default function NewSession() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [f, setF] = useState({ project_id: "", objective_id: "", engine: "", model: "" });
  const [cap, setCap] = useState(String(DEFAULT_CAP));
  const { data: projects, isLoading } = useQuery({ queryKey: ["projects"], queryFn: async () => (await api.get("/projects")).data });
  const { data: objs } = useQuery({ queryKey: ["objectives"], queryFn: async () => (await api.get("/objectives")).data });
  const { data: eng } = useQuery({ queryKey: ["engines"], queryFn: async () => (await api.get("/engines")).data });
  const { data: memory } = useQuery({ queryKey: ["memory", f.project_id], enabled: !!f.project_id,
                                      queryFn: async () => (await api.get(`/memory?project_id=${f.project_id}`)).data });
  useEffect(() => {
    if (projects?.projects?.length) setF((x) => (x.project_id ? x : { ...x, project_id: projects.projects[0].id }));
  }, [projects]);
  const engines = (eng?.engines || []).map((e) => ({ ...e, disabled: !e.available || !e.models.length }));
  useEffect(() => {  // the first engine that can run, with its default model
    const first = engines.find((e) => !e.disabled);
    if (first && !f.engine) setF((x) => ({ ...x, engine: first.engine, model: first.models[0].model_id }));
  }, [eng]);  // eslint-disable-line react-hooks/exhaustive-deps

  const create = useMutation({
    mutationFn: async () => {
      const { session } = (await api.post("/sessions", { ...f, objective_id: f.objective_id || null })).data;
      if (Number(cap) !== DEFAULT_CAP) await api.put(`/sessions/${session.id}/cap`, { budget_cap_usd: Number(cap) });
      return session;
    },
    onSuccess: (s) => { toast.success("Session created."); navigate(`/team/session/${s.id}`); },
    onError: (e) => toast.error(apiErr(e)),
  });

  // every kind of work but code gets plain words: an agent, a copy of the files, a budget (design_handoff_documents_view)
  const kind = kindOf(user);
  const docs = isDocuments(kind);
  const header = <PageHeader eyebrow="New session" title={`Pick what you're working on and the ${docs ? "agent" : "engine"} to use.`}
    status={`${KIND_LABELS[kind]}: it opens the ${docs ? "documents view" : "terminal"}. It starts locked, and only you can unlock it.`} />;
  if (isLoading) return <Page>{header}<Loading /></Page>;
  if (!projects?.projects?.length) return (
    // a coder can join before the company is set up: projects are the owner's to create
    <Page data-testid="coder-new-session">{header}
      <Card className="px-5 py-6 flex flex-col gap-1" data-testid="no-projects">
        <span className="font-display text-[15px] tracking-[0.02em] font-semibold">No project yet</span>
        <span className="text-muted-foreground">Your company's founder is still setting things up. You can start a session as soon as they create a project.</span>
      </Card>
    </Page>
  );

  const project = projects.projects.find((p) => p.id === f.project_id);
  const projObjs = (objs?.objectives || []).filter((o) => o.project_id === f.project_id && o.status !== "done");
  const objective = projObjs.find((o) => o.id === f.objective_id);
  const engine = engines.find((e) => e.engine === f.engine);
  const credit = engine?.models.find((m) => m.model_id === f.model)?.credit_prices_per_1m;
  const capOk = Number(cap) >= 1;
  const ready = capOk && f.project_id && f.model && engine && !engine.disabled;
  const doc = memory?.memory;
  const rules = (doc?.rules || "").split("\n").filter((l) => l.trim()).length;
  const handle = user?.handle || (user?.email || "").split("@")[0];
  const engineSub = (e) => e.reason || (e.billing === "credits" ? `On ${APP_NAME} credits` : e.billing ? `On your company's ${PROVIDER[e.engine]} key` : "Runs without an API key");
  return (
    <Page data-testid="coder-new-session">
      {header}
      <div className="flex flex-wrap gap-5 items-start">
        <Card className="flex-[100_1_440px] min-w-0 p-5 flex flex-col gap-[22px]">
          <div className="flex flex-col gap-2">
            <span className="text-sm font-semibold">Project</span>
            <Segmented label="Project" className="self-start" options={projects.projects.map((p) => [p.id, p.name])} value={f.project_id}
              onChange={(v) => setF({ ...f, project_id: v, objective_id: "" })} testid="new-session-project" />
          </div>
          <div role="radiogroup" aria-label="Objective" className="flex flex-col gap-2">
            <span className="text-sm font-semibold">Objective</span>
            {projObjs.map((o) => <ObjectiveOption key={o.id} o={o} on={f.objective_id === o.id} onPick={() => setF({ ...f, objective_id: o.id })} />)}
            <Option on={!f.objective_id} onPick={() => setF({ ...f, objective_id: "" })} testid="new-session-objective-general">
              <Radio on={!f.objective_id} />
              <span className="flex flex-col"><span className="font-semibold">General</span>
                <span className="text-sm text-muted-foreground">{projObjs.length ? "Not linked to an objective" : "No open objectives on this project"}</span></span>
            </Option>
          </div>
          <div role="radiogroup" aria-label={docs ? "Agent" : "Engine"} className="flex flex-col gap-2">
            <span className="text-sm font-semibold">{docs ? "Agent" : "Engine"}</span>
            <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-2">
              {engines.map((e) => (
                <Option key={e.engine} on={f.engine === e.engine} disabled={e.disabled} testid={`engine-option-${e.engine}`}
                  onPick={() => setF({ ...f, engine: e.engine, model: e.models[0]?.model_id || "" })}>
                  <EngineMark engine={e.engine} className="w-5 h-5 mt-px" />
                  <span className="flex flex-col min-w-0">
                    <span className="font-semibold">{ENGINE_LABELS[e.engine] || e.engine}</span>
                    <span className="text-sm text-muted-foreground">{engineSub(e)}</span>
                    {f.engine === e.engine && f.model && (docs
                      ? <span className="text-sm text-muted-foreground">{e.models.find((m) => m.model_id === f.model)?.label || f.model}</span>
                      : <span className="font-mono text-xs text-muted-foreground">{f.model}</span>)}
                  </span>
                </Option>
              ))}
            </div>
            {engine && engine.models.length > 1 && (
              <Segmented label="Model" className="self-start" options={engine.models.map((m) => [m.model_id, m.label])} value={f.model}
                onChange={(v) => setF({ ...f, model: v })} testid="new-session-model" />
            )}
            {credit && (
              <span className="text-sm text-muted-foreground" data-testid="new-session-credit-price">
                On {APP_NAME} credits: {usd(credit.input)} input · {usd(credit.output)} output per 1M tokens.
              </span>
            )}
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="ns-cap" className="text-sm font-semibold">{docs ? "Session budget" : "Session cap"}</label>
            <label className="flex items-center gap-1.5 self-start h-8 px-2.5 rounded-md border border-gr-control-border focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-ring">
              <span className="text-muted-foreground">$</span>
              <input id="ns-cap" type="number" min="1" step="1" value={cap} onChange={(e) => setCap(e.target.value)} data-testid="new-session-cap"
                className="w-[72px] border-0 bg-transparent text-[13px] outline-none tabular-nums" />
            </label>
            <span className="text-sm text-muted-foreground">The session stops when it reaches this. You can change it later from the session.</span>
          </div>
        </Card>

        <Card className="flex-[1_0_280px] max-w-full p-5 flex flex-col gap-3.5" data-testid="new-session-summary">
          <span className="font-display text-[15px] tracking-[0.02em] font-semibold">What the session starts with</span>
          {docs ? (
            <div className="flex flex-col gap-0.5" data-testid="new-session-copy">
              <Label11>Its own copy</Label11>
              <span className="[text-wrap:pretty]">Every change is saved as a version you can look back at.</span>
              <span className="text-sm text-muted-foreground">A copy of {project?.name || "the project"}'s files, only for this session.</span>
            </div>
          ) : (
            <div className="flex flex-col gap-0.5">
              <Label11>Its own branch</Label11>
              <span className="font-mono text-[12px]">s/{handle}/<span className="text-muted-foreground" aria-label="its id">······</span></span>
              <span className="text-sm text-muted-foreground">from {project?.base_branch || "main"}, committed for you after each turn</span>
            </div>
          )}
          {project?.my_folders && (
            <div className="flex flex-col gap-0.5" data-testid="new-session-folders">
              <Label11>Only these folders</Label11>
              <span className="font-mono text-[12px] [overflow-wrap:anywhere]">{project.my_folders.join(", ")}</span>
              <span className="text-sm text-muted-foreground [text-wrap:pretty]">The founder limited you to them in this project: only they reach the
                session, and changes anywhere else are put back.</span>
            </div>
          )}
          <div className="flex flex-col gap-0.5">
            <Label11>Its goal</Label11>
            <span className="[text-wrap:pretty]">{objective
              ? [objective.description, objective.success_criteria && `Done when ${objective.success_criteria.replace(/^./, (c) => c.toLowerCase())}`].filter(Boolean).join(" ") || objective.title
              : "No objective. The session gets the project's shared memory only."}</span>
          </div>
          <div className="flex flex-col gap-0.5">
            <Label11>Shared memory</Label11>
            <span>{doc ? `Version ${doc.version} · ${rules} ${rules === 1 ? "rule" : "rules"} and ${(doc.knowledge || []).length} ${(doc.knowledge || []).length === 1 ? "entry" : "entries"}` : "—"}</span>
          </div>
          <div className="flex flex-col gap-1.5 mt-1">
            <Button className="w-full" aria-disabled={!ready} disabled={create.isPending} onClick={() => ready && create.mutate()} data-testid="new-session-start-button">Create session</Button>
            {!capOk && <span className="text-sm text-muted-foreground">Set a {docs ? "budget" : "cap"} of at least $1.</span>}
            {capOk && engine?.disabled && <span className="text-sm text-muted-foreground">Pick an {docs ? "agent" : "engine"} that can run.</span>}
          </div>
        </Card>
      </div>
    </Page>
  );
}
